import { useState, useEffect } from 'react';
import { useTranslation } from '../../../components/Translation';
import type { LabEvent } from '../../../constants/events/events';

const statusColors: Record<string, string> = {
  destacado: "bg-dorado-uady text-white",
  proximo: "bg-blue-500 text-white",
  pasado: "bg-gray-400 text-white",
};

export default function EventModal({ event, onClose }: { event: LabEvent; onClose: () => void }) {
  const { t } = useTranslation();

  const allMedia = [{ type: "image" as const, src: event.imageSrc }, ...(event.gallery || [])];
  const [activeIndex, setActiveIndex] = useState(0);
  const hasMultiple = allMedia.length > 1;

  const goPrev = () => setActiveIndex((prev) => (prev - 1 + allMedia.length) % allMedia.length);
  const goNext = () => setActiveIndex((prev) => (prev + 1) % allMedia.length);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasMultiple) goPrev();
      if (e.key === "ArrowRight" && hasMultiple) goNext();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose, hasMultiple]);

  const title = t(`eventos.${event.titleKey}`) !== `eventos.${event.titleKey}` ? t(`eventos.${event.titleKey}`) : event.titleText;
  const description = t(`eventos.${event.descriptionKey}`) !== `eventos.${event.descriptionKey}` ? t(`eventos.${event.descriptionKey}`) : event.descriptionText;

  const formattedDate = new Date(event.date).toLocaleDateString(
    t('eventos.locale') !== 'eventos.locale' ? t('eventos.locale') : 'es-MX',
    { year: 'numeric', month: 'long', day: 'numeric' }
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(13, 31, 53, 0.5)" }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl overflow-hidden flex flex-col md:flex-row w-full max-w-5xl h-[65vh] relative border border-white/60"
        style={{
          background: "rgba(255,255,255,0.65)",
          backdropFilter: "blur(24px) saturate(180%)",
          WebkitBackdropFilter: "blur(24px) saturate(180%)",
          boxShadow: "0 8px 40px rgba(13,31,53,0.25), inset 0 1px 0 rgba(255,255,255,0.8)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors"
        >
          ✕
        </button>

        {/* Galería — izquierda */}
        <div className="w-full md:w-2/5 flex-shrink-0 flex flex-col bg-black/5">
          <div className="relative flex-1 min-h-64 md:min-h-0 bg-black/10">
            <span className={`absolute top-3 left-3 z-10 text-micro font-bold px-3 py-1 rounded-full ${statusColors[event.status]}`}>
              {t(`eventos.status_${event.status}`)}
            </span>

            {allMedia[activeIndex].type === "video" ? (
              <video
                key={allMedia[activeIndex].src}
                src={allMedia[activeIndex].src}
                controls
                className="w-full h-full object-contain"
              />
            ) : (
              <img
                src={allMedia[activeIndex].src}
                alt={title}
                className="w-full h-full object-contain"
              />
            )}

            {hasMultiple && (
              <>
                <button
                  onClick={goPrev}
                  className="absolute top-1/2 left-2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors"
                  aria-label="Anterior"
                >
                  ❮
                </button>
                <button
                  onClick={goNext}
                  className="absolute top-1/2 right-2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors"
                  aria-label="Siguiente"
                >
                  ❯
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-micro font-semibold px-2 py-0.5 rounded-full">
                  {activeIndex + 1} / {allMedia.length}
                </div>
              </>
            )}
          </div>

          {hasMultiple && (
            <div className="flex gap-2 p-3 overflow-x-auto bg-black/10">
              {allMedia.map((item, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === activeIndex ? "border-dorado-uady" : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                >
                  {item.type === "video" ? (
                    <video src={item.src} className="w-full h-full object-cover" muted />
                  ) : (
                    <img src={item.src} alt="" className="w-full h-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Contenido — derecha */}
        <div className="flex-1 p-6 md:p-8 overflow-y-auto flex flex-col">
          {event.categoryKey && (
            <span className="text-micro font-bold uppercase text-dorado-uady tracking-widest mb-2">
              {t(`eventos.${event.categoryKey}`)}</span>
          )}
          <h2 className="text-xl md:text-2xl font-bold text-azul-uady mb-3 leading-snug">
            {title}
          </h2>

          <div className="flex items-center gap-2 text-mini text-gray-500 mb-1">
            <span>📅</span>
            <span className="capitalize">{formattedDate}</span>
          </div>
          {event.location && (
            <div className="flex items-center gap-2 text-mini text-gray-500 mb-4">
              <span>📍</span>
              <span>{event.location}</span>
            </div>
          )}

          <p className="text-mini text-gray-600 leading-relaxed text-justify">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}
